import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, User, Utensils, Wine, BedDouble, Wrench, PartyPopper, ShieldCheck, ArrowRight, Sparkles, Lock, LogOut } from "lucide-react";
import { Logo } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";

const PORTALS = [
  { key: "guest", role: "guest", route: "/customer", label: "Customer Portal", icon: User, grad: "from-[#FF5E36] to-[#FF2A6D]",
    desc: "Discover locations, submit reservation requests and raise & track service requests." },
  { key: "kitchen", dept: "Kitchen", route: "/kitchen", label: "Kitchen", icon: Utensils, grad: "from-[#F6B73C] to-[#FF6B35]",
    desc: "Kitchen bookings, allergy alerts, tasks, handovers and escalations." },
  { key: "fnb", dept: "Food & Beverage", route: "/food-beverage", label: "Food & Beverage", icon: Wine, grad: "from-[#FF6B35] to-[#FF2A6D]",
    desc: "Food & Beverage bookings, allergy alerts, tasks and handovers." },
  { key: "housekeeping", dept: "Housekeeping", route: "/housekeeping", label: "Housekeeping", icon: BedDouble, grad: "from-[#00D2FF] to-[#0078FF]",
    desc: "Room & service tasks, handovers and escalations." },
  { key: "maintenance", dept: "Maintenance", route: "/maintenance", label: "Maintenance", icon: Wrench, grad: "from-[#A855F7] to-[#7F00FF]",
    desc: "Work orders, scheduled jobs, handovers and escalations." },
  { key: "events", dept: "Events", route: "/events", label: "Events", icon: PartyPopper, grad: "from-[#FF2A6D] to-[#A855F7]",
    desc: "Event bookings, tasks, handovers and escalations." },
  { key: "reception", role: "reception", route: "/reception", label: "Reception", icon: Building2, grad: "from-[#00D2FF] to-[#0078FF]",
    desc: "Manage the booking lifecycle and triage guest complaints." },
  { key: "admin", role: "admin", route: "/admin", label: "Management", icon: ShieldCheck, grad: "from-[#FF6B35] to-[#FF2A6D]",
    desc: "Cross-location oversight, assign complaints and view operational analytics." },
];

export default function PortalSelection() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (user && user.role === "superadmin") navigate("/platform", { replace: true });
  }, [user, navigate]);

  // A card is "owned" when it belongs to the user's role/department.
  const isOwn = (p) => {
    if (!user) return false;
    if (p.role) return user.role === p.role;
    if (p.dept) return ["staff", "manager"].includes(user.role) && user.department_name === p.dept;
    return false;
  };
  // Management (admin) can open every portal.
  const canAccess = (p) => !!user && (user.role === "admin" || isOwn(p));

  const go = (p) => {
    if (!user) { navigate(p.key === "guest" ? "/login?portal=guest" : "/login"); return; }
    if (canAccess(p)) navigate(p.route);
  };

  const signOut = async () => { await logout(); navigate("/"); };

  return (
    <div className="bd-page">
      <header className="flex items-center justify-between px-6 lg:px-12 py-6">
        <button onClick={() => navigate("/")}><Logo /></button>
        {user
          ? <button onClick={signOut} data-testid="portals-signout" className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-200 hover:text-white transition-colors"><LogOut size={16} /> Sign out</button>
          : <button onClick={() => navigate("/login")} data-testid="header-signin" className="text-sm font-semibold text-purple-200 hover:text-white transition-colors">Sign in</button>}
      </header>

      <section className="text-center px-6 pt-10 pb-6 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#451B7F] bg-[#180735]/60 text-xs font-semibold text-purple-200 mb-6">
          <Sparkles size={14} className="text-[#FF6B35]" /> Hospitality Operations Platform
        </div>
        <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
          {user
            ? <>Welcome back, <span className="bd-grad-text">{user.name?.split(" ")[0]}</span>.</>
            : <>One place where guest issues<br /> reach the <span className="bd-grad-text">right people</span>.</>}
        </h1>
        <p className="mt-5 text-base text-purple-200/70 max-w-xl mx-auto leading-relaxed">
          {user
            ? (user.role === "admin"
                ? "As management you can open any portal below."
                : "Open the portal your account has access to. Other portals are shown but locked.")
            : "Sign in to open your portal. You'll only ever access the portal your account is permitted to use."}
        </p>
      </section>

      <section className="px-6 lg:px-12 pb-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {PORTALS.map((p, i) => {
          const accessible = canAccess(p);
          const locked = user && !accessible;
          const owned = isOwn(p);
          return (
            <motion.button
              key={p.key} data-testid={`portal-card-${p.key}`} onClick={() => go(p)} disabled={locked}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className={`group relative text-left rounded-2xl bg-[#180735] border p-6 shadow-xl overflow-hidden transition-colors duration-300 ${locked ? "border-[#2E1256] opacity-40 cursor-not-allowed" : "border-[#2E1256] hover:border-[#FF5E36]/50"}`}>
              {locked && <span className="absolute top-4 right-4 text-purple-300" data-testid={`portal-locked-${p.key}`}><Lock size={16} /></span>}
              {user && !locked && owned && <span className="absolute top-4 right-4 text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full text-white bg-gradient-to-r from-[#FF6B35] to-[#FF2A6D]">Your portal</span>}
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${p.grad} flex items-center justify-center shadow-lg mb-5`}>
                <p.icon size={26} className="text-white" />
              </div>
              <h3 className="font-head text-lg font-bold text-white mb-2">{p.label}</h3>
              <p className="text-sm text-purple-300/70 leading-relaxed mb-5">{p.desc}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF6B7D] group-hover:gap-3 transition-all">
                {locked ? "No access" : user ? "Enter portal" : "Sign in"} {!locked && <ArrowRight size={16} />}
              </span>
            </motion.button>
          );
        })}
      </section>
    </div>
  );
}
